import { Resources } from 'constants/resources';

import ContentCreate from '@mui/icons-material/Create';
import { usePermissions } from 'hooks/usePermissions';
import { useRecordModal } from 'hooks/useRecordModal';
import { PresetsDeleteDialog } from 'pages/Presets/components/PresetsDeleteDialog';
import { PresetsEditGeneralDialog } from 'pages/Presets/components/PresetsEditGeneralDialog';
import { useCreatePath, useListContext } from 'react-admin';
import { Link } from 'react-router-dom';
import { Alert } from 'shared/mui/Alert/Alert';
import {
  ContextMenu,
  ContextMenuButton,
  ContextMenuDeleteButton,
  ContextMenuShowButton,
} from 'shared/mui/ContextMenu';
import { DatagridStickyContainer } from 'shared/react-admin/Datagrids/DatagridSticky/DatagridStickyContainer';
import { DateTimeField, FunctionField } from 'shared/react-admin/Fields';
import { CurrencyField } from 'shared/react-admin/Fields/CurrencyField/CurrencyField';
import { EmbeddedFunctionField } from 'shared/react-admin/Fields/Embedded/EmbeddedFunctionField/EmbeddedFunctionField';
import { TextLinkField } from 'shared/react-admin/Fields/TextLinkField/TextLinkField';
import { Permission } from 'types/embedded/permission';
import { Preset } from 'types/embedded/preset';

import { PresetsListAmountChips } from './PresetsListAmountChips';
import { DatagridStyled } from './PresetsListContent.styled';
import { PresetsListFilters } from './PresetsListFilter/PresetsListFilters';

export const PresetsListContent: React.FC = () => {
  const { data, isLoading, filterValues } = useListContext();
  const { hasResourcePermissions } = usePermissions();
  const createPath = useCreatePath();
  const deleteModal = useRecordModal<Preset>();
  const editGeneralModal = useRecordModal<Preset>();

  const canEdit = hasResourcePermissions(Resources.Presets, [Permission.Full, Permission.Update]);
  const canDelete = hasResourcePermissions(Resources.Presets, [Permission.Full, Permission.Delete]);

  const hasFilters = Object.keys(filterValues).length > 0;
  const showAlert = !isLoading && !data?.length && !hasFilters;

  if (showAlert) {
    return (
      <Alert severity="info">
        Нет созданных пресетов для доступных мерчантов. Создайте пресеты, нажав кнопку
        &quot;Добавить&quot;.
      </Alert>
    );
  }

  return (
    <>
      <PresetsListFilters />
      <DatagridStickyContainer>
        <DatagridStyled bulkActionButtons={false}>
          <TextLinkField label="ID" resource="presets" sortBy="id" source="originId" type="show" />
          <EmbeddedFunctionField
            label="Мерчант"
            linkConfig={{ resource: 'merchants', idField: 'merchant.@id' }}
            sourceField="merchant.name"
          />
          <EmbeddedFunctionField
            label="Шлюз"
            linkConfig={{ resource: 'gateways', idField: 'gateway.@id' }}
            sourceField="gateway.name"
          />
          <CurrencyField
            alphaCodeSource="currency.alphaCode"
            label="Валюта"
            nameSource="currency.name"
          />
          <FunctionField
            label="Пресеты суммы пополнения"
            render={(record: Preset) => (
              <PresetsListAmountChips items={record.paymentItems ?? []} />
            )}
          />
          <FunctionField
            label="Пресеты суммы вывода"
            render={(record: Preset) => <PresetsListAmountChips items={record.payoutItems ?? []} />}
          />
          <DateTimeField label="Дата создания" sortBy="createdAt" source="createdAt" />
          <DateTimeField label="Дата редактир." sortBy="updatedAt" source="updatedAt" />
          <ContextMenu
            render={(record: Preset) => [
              <ContextMenuShowButton key="show-button" />,
              canEdit && (
                <ContextMenuButton
                  icon={<ContentCreate fontSize="small" style={{ marginTop: '1px' }} />}
                  key="edit-general-button"
                  label="Редактировать общую информацию"
                  onClick={() => editGeneralModal.handleOpenRecordModal(record)}
                />
              ),
              canEdit && (
                <Link
                  key="edit-payment-button"
                  style={{ textDecoration: 'none' }}
                  to={`${createPath({ resource: Resources.Presets, id: record.id, type: 'edit' })}/payment_items`}
                >
                  <ContextMenuButton
                    icon={<ContentCreate fontSize="small" style={{ marginTop: '1px' }} />}
                    label="Редактировать пресеты пополнения"
                  />
                </Link>
              ),
              canEdit && (
                <Link
                  key="edit-payout-button"
                  style={{ textDecoration: 'none' }}
                  to={`${createPath({ resource: Resources.Presets, id: record.id, type: 'edit' })}/payout_items`}
                >
                  <ContextMenuButton
                    icon={<ContentCreate fontSize="small" style={{ marginTop: '1px' }} />}
                    label="Редактировать пресеты вывода"
                  />
                </Link>
              ),
              canDelete && (
                <ContextMenuDeleteButton
                  key="delete-button"
                  onClick={() => deleteModal.handleOpenRecordModal(record)}
                />
              ),
            ]}
          />
        </DatagridStyled>
      </DatagridStickyContainer>
      {deleteModal.openRecordModal && (
        <PresetsDeleteDialog
          onClose={deleteModal.handleCloseRecordModal}
          onSuccess={deleteModal.handleCloseRecordModal}
          open={deleteModal.openRecordModal}
          record={deleteModal.record}
        />
      )}
      {editGeneralModal.openRecordModal && editGeneralModal.record && (
        <PresetsEditGeneralDialog
          onClose={editGeneralModal.handleCloseRecordModal}
          open={editGeneralModal.openRecordModal}
          record={editGeneralModal.record}
        />
      )}
    </>
  );
};
